import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useEditor } from '@tiptap/react';

import { EditorState } from 'prosemirror-state';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { cn } from '@/utils/tailwind';

import { extractRemainder } from './utils';

interface CustomEditorState extends EditorState {
  mention$: {
    active: boolean;
    composing: boolean;
    decorationId: string;
    query: string;
  };
}

interface SuggestionsListProps {
  items: string[];
  command: (item: { id: string }) => void;
  editor: ReturnType<typeof useEditor>;
  endChars?: string[];
  listTitle?: string;
  emptyStateMessage?: string;
}

export const SuggestionsList = forwardRef<
  { onKeyDown: (event: { event: KeyboardEvent }) => boolean },
  SuggestionsListProps
>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const currentInput = (props.editor?.state as CustomEditorState).mention$
    .query;
  const currentInputEndsWithEndChar = props.endChars?.some((endChar) =>
    currentInput.endsWith(endChar)
  );

  const selectItem = useCallback(
    (index: number) => {
      const item = props.items[index];
      if (item && props.editor) {
        const { view, commands } = props.editor;
        const state = props.editor.state as CustomEditorState;
        const position = state.selection.from;
        const text = extractRemainder(state.mention$.query, item);
        if (text !== '') {
          commands.insertContentAt(position, {
            type: 'text',
            text: text,
          });
        }
        view.focus();
      }
    },
    [props.editor, props.items]
  );

  useEffect(() => setSelectedIndex(0), [props.items]);

  useEffect(() => {
    const selectedItem = document.querySelector('.selected');
    if (selectedItem) {
      selectedItem.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp' && !currentInputEndsWithEndChar) {
          const nextIndex = selectedIndex - 1;
          if (nextIndex < 0) {
            setSelectedIndex(props.items.length - 1);
          } else {
            setSelectedIndex(nextIndex);
          }
          return true;
        }
        if (event.key === 'ArrowDown' && !currentInputEndsWithEndChar) {
          const nextIndex = selectedIndex + 1;

          if (nextIndex < props.items.length) {
            setSelectedIndex(nextIndex);
          } else {
            setSelectedIndex(0);
          }
          return true;
        }
        if (event.key === 'Enter' && !currentInputEndsWithEndChar) {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }),
    [selectedIndex, currentInputEndsWithEndChar, props.items, selectItem]
  );

  return (
    <div
      className={cn(
        'w-[225px] overflow-hidden rounded-lg bg-white text-sm text-gray-800 shadow-md dark:bg-[#0C111D]',
        (!currentInputEndsWithEndChar && !props.items.length) ??
          'border border-secondary'
      )}
    >
      <div className="max-h-[200px] w-full overflow-auto dark:bg-[#0C111D] dark:text-white">
        {props.items.length ? (
          <div className="overflow-auto">
            <div className="text-tertiar p-2 text-xs">
              {props.listTitle ?? 'Recent'}
            </div>
            {props.items.map((item, index) => (
              <button
                type="button"
                className={`w-full p-2 text-left ${
                  index === selectedIndex
                    ? 'selected bg-gray-100 dark:bg-[#1F242F]'
                    : ''
                }`}
                key={index}
                onClick={() => selectItem(index)}
              >
                {item}
              </button>
            ))}
          </div>
        ) : (
          !currentInputEndsWithEndChar && (
            <div className="flex flex-row gap-1 bg-brand-primary bg-opacity-10 p-2 text-brand-green-400">
              <InformationCircleIcon className="my-auto h-4 w-4 stroke-brand-green-400" />
              {props.emptyStateMessage ?? 'Key not found in recent runs'}
            </div>
          )
        )}
      </div>
    </div>
  );
});
